//Built-in libraries
import { ChangeEvent } from "react";

//Third party libraries

//Custom components
import Button from "components/UI/Button";
import Input from "components/UI/Form/Input";
import Modal, { ModalProps } from "components/UI/Modal";
import ReactSelect from "components/UI/Form/ReactSelect";
import RadioGroupInput from "components/UI/Form/RadioGroup";
import { preliminaryPlan } from "../OrganizationalGoals/constants";

//Services
import { ReactSelectOption } from "types/Constants";
import { States } from "../../../../../../constants";

//Assets andd Miscellaneous

//Component Begin

interface IAddPropertyFormProps extends ModalProps {
  modalHandler: () => void;
  address: { [key: string]: { [key: string]: any } };
  inputChangedHandler: (
    value:
      | string
      | number
      | boolean
      | ChangeEvent<HTMLInputElement>
      | undefined,
    name: string
  ) => void;
  onAddPropertyHandler: () => void;
  isEditing: boolean;
  updatePropertyHandler: () => void;
}

export default function AddPropertyForm({
  modalHandler,
  isEditing,
  address,
  inputChangedHandler,
  onAddPropertyHandler,
  updatePropertyHandler,
  ...rest
}: IAddPropertyFormProps) {
  return (
    <Modal width="min-w-2/3">
      <h2 className="mt-0">{`${
        isEditing ? "Edit" : "Add"
      } Property Details`}</h2>
      <div className="space-y-6">
        <section className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <Input
              label="Property Name"
              id="property-name"
              name="propertyName"
              type="text"
              value={address?.propertyName.value}
              error={address?.propertyName?.error}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                inputChangedHandler(e.target.value, e.target.name);
              }}
              onBlur={(e: ChangeEvent<HTMLInputElement>) => {
                inputChangedHandler(e.target.value, e.target.name);
              }}
            />
            <Input
              label="Current Use"
              id="current-use"
              name="currentUse"
              type="text"
              value={address?.currentUse.value}
              error={address?.currentUse?.error}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                inputChangedHandler(e.target.value, e.target.name);
              }}
              onBlur={(e: ChangeEvent<HTMLInputElement>) => {
                inputChangedHandler(e.target.value, e.target.name);
              }}
            />
            <div>
              <ReactSelect
                label="Do you have a future use for this property"
                name="futureUse"
                options={preliminaryPlan.map((s) => ({
                  label: s,
                  value: s,
                }))}
                value={{
                  label: address?.futureUse.value,
                  value: address?.futureUse.value,
                }}
                onChange={(option) => {
                  const reactSelectOption = option as ReactSelectOption;

                  inputChangedHandler(reactSelectOption.value, "futureUse");
                }}
              />
            </div>
            <div className="col-span-3">
              <Input
                label="Street Address"
                id="street-address"
                name="streetAddress"
                type="text"
                value={address?.streetAddress.value}
                error={address?.streetAddress?.error}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  inputChangedHandler(e.target.value, e.target.name);
                }}
                onBlur={(e: ChangeEvent<HTMLInputElement>) => {
                  inputChangedHandler(e.target.value, e.target.name);
                }}
              />
            </div>

            <Input
              label="City"
              name="city"
              type="text"
              value={address?.city.value}
              error={address?.city?.error}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                inputChangedHandler(e.target.value, e.target.name);
              }}
              onBlur={(e: ChangeEvent<HTMLInputElement>) => {
                inputChangedHandler(e.target.value, e.target.name);
              }}
            />
            <div>
              <ReactSelect
                label="State"
                name="state"
                value={{
                  label: address?.state.value,
                  value: address?.state.value,
                }}
                options={States.map((s) => ({ label: s.name, value: s.name }))}
                onChange={(option) => {
                  const reactSelectOption = option as ReactSelectOption;
                  inputChangedHandler(reactSelectOption.value, "state");
                }}
              />
            </div>
            <Input
              label="Zip/Postal Code"
              id="zipCode"
              name="zipCode"
              type="text"
              value={address?.zipCode.value}
              error={address?.zipCode?.error}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                inputChangedHandler(e.target.value, e.target.name);
              }}
              onBlur={(e: ChangeEvent<HTMLInputElement>) => {
                inputChangedHandler(e.target.value, e.target.name);
              }}
            />

            <div className="col-span-3">
              <RadioGroupInput
                label="Are you aware of any outstanding code violations or municipal liens on the property(s) of interest?"
                id="code-violation"
                name="codeViolation"
                options={[
                  { label: "Yes", value: "yes" },
                  { label: "No", value: "no" },
                ]}
                value={address?.codeViolation.value}
                error={address?.codeViolation.error}
                onChange={(value) => {
                  inputChangedHandler(value, "codeViolation");
                }}
              />
            </div>
          </div>
        </section>
        <section className="space-y-2">
          <h4 className="font-semibold">
            Are you up to date on this property ?
          </h4>
          <div className="space-y-2">
            <RadioGroupInput
              label="Property Taxes"
              id="property-taxes"
              name="propertyTaxes"
              options={[
                { label: "Yes", value: "yes" },
                { label: "No", value: "no" },
                { label: `Don't Know`, value: `don't know` },
              ]}
              value={address?.propertyTaxes.value}
              error={address?.propertyTaxes.error}
              onChange={(value) => {
                inputChangedHandler(value, "propertyTaxes");
              }}
            />
            <RadioGroupInput
              label="Water Bills"
              id="water-bills"
              name="waterBills"
              options={[
                { label: "Yes", value: "yes" },
                { label: "No", value: "no" },
                { label: `Don't Know`, value: `don't know` },
              ]}
              value={address?.waterBills.value}
              error={address?.waterBills.error}
              onChange={(value) => {
                inputChangedHandler(value, "waterBills");
              }}
            />
            <RadioGroupInput
              label="Sewage Bills"
              id="sewage-bills"
              name="sewageBills"
              options={[
                { label: "Yes", value: "yes" },
                { label: "No", value: "no" },
                { label: `Don't Know`, value: `don't know` },
              ]}
              value={address?.sewageBills.value}
              error={address?.sewageBills.error}
              onChange={(value) => {
                inputChangedHandler(value, "sewageBills");
              }}
            />
            <RadioGroupInput
              label="Refuse Bills"
              id="refuse-bills"
              name="refuseBills"
              options={[
                { label: "Yes", value: "yes" },
                { label: "No", value: "no" },
                { label: `Don't Know`, value: `don't know` },
              ]}
              value={address?.refuseBills.value}
              error={address?.refuseBills.error}
              onChange={(value) => {
                inputChangedHandler(value, "refuseBills");
              }}
            />
          </div>
        </section>
        <div className="flex justify-end gap-4 mt-10">
          <Button
            disabled={false}
            label="Cancel"
            type="button"
            btnStyle="neutral"
            onClick={modalHandler}
          />
          <Button
            label={`${isEditing ? "Save" : "Add"}`}
            type="button"
            btnStyle="primary"
            onClick={isEditing ? updatePropertyHandler : onAddPropertyHandler}
          />
        </div>
      </div>
    </Modal>
  );
}
